import React, { useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Input,
} from "@heroui/react";

import { Icon } from "@iconify/react";
import { motion } from "framer-motion";
import { useGoogleLogin } from "@react-oauth/google";
import { Capacitor } from "@capacitor/core";
import { SocialLogin } from "@capgo/capacitor-social-login";
import { client } from "@/client";
import { authClient, useIsLogin } from "@/openbaas";
import { messagePromise } from "@/utils/messagePromise";

interface LoginFormProps {
  onSubmit: (email: string) => void;
  onRegister: () => void; // Add this prop
}
const isNative = Capacitor.isNativePlatform();

export const LoginForm: React.FC<LoginFormProps> = ({ onSubmit }) => {
  useEffect(() => {
    SocialLogin.initialize({
      google: {
        webClientId: client.GOOGLE,
        mode: "online",
      },
    });
  }, []);
  const { setIsLogin } = useIsLogin();
  const [email, setEmail] = React.useState("");
  const googleLogin = isNative
    ? async () => {
        try {
          const user = await SocialLogin.login({
            provider: "google",
            options: {},
          });

          if (user.provider == "google") {
            messagePromise(
              new Promise(async (resolve, reject) => {
                try {
                  await authClient.signIn.social({
                    provider: "google",
                    idToken: {
                      //@ts-ignore
                      token: user.result.idToken,
                      //@ts-ignore
                      accessToken: user.result.accessToken.token,
                    },
                  });
                  setIsLogin(true);
                  resolve(null);
                } catch (error) {
                  console.error(error);
                  reject(error);
                }
              }),
              {
                pending: "Iniciando sesión con Google...",
                success: "Sesión iniciada correctamente",
                error: "Error al iniciar sesión con Google",
              }
            );
          }
        } catch (err) {
          console.error("Error en login nativo", err);
        }
      }
    : useGoogleLogin({
        onSuccess: async (codeResponse) => {
          messagePromise(
            new Promise(async (resolve, reject) => {
              try {
                const res = await fetch(`${client.API}/auth/google`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ code: codeResponse.code }),
                });

                const { id_token, access_token } = await res.json();

                await authClient.signIn.social({
                  provider: "google",
                  idToken: {
                    token: id_token,
                    accessToken: access_token,
                  },
                });

                setIsLogin(true);
                resolve(null);
              } catch (error) {
                console.error("Error durante el login con Google:", error);
                reject(error); // Para que `messagePromise` pueda capturar el error
              }
            }),
            {
              pending: "Iniciando sesión con Google...",
              success: "Sesión iniciada correctamente",
              error: "Error al iniciar sesión con Google",
            }
          );
        },
        flow: "auth-code",
      });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      onSubmit(email);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <Card className="border-none shadow-lg">
        <CardHeader className="flex flex-col items-center gap-2 pb-0">
          <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mb-2">
            <img
              className="w-20 h-20 rounded-full"
              src={"/icons/logo192.webp"}
            />
          </div>
          <h1 className="text-2xl font-bold text-center">
            Supermercados D'ley
          </h1>
          <p className="text-default-500 text-center text-sm">
            Inicia sesión en tu cuenta para continuar
          </p>
        </CardHeader>

        <CardBody className="py-5">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Correo Electrónico"
              placeholder="Ingresa tu correo"
              type="email"
              variant="bordered"
              value={email}
              onValueChange={setEmail}
              isRequired
              startContent={
                <Icon icon="lucide:mail" className="text-default-400 w-5 h-5" />
              }
              classNames={{
                input: "text-sm",
                label: "text-sm",
              }}
            />
            <Button
              type="submit"
              color="primary"
              className="w-full font-medium"
              size="lg"
            >
              Iniciar sesión con correo
            </Button>
          </form>

          <div className="flex items-center gap-4 my-4">
            <Divider className="flex-1" />
            <p className="text-tiny text-default-500">O continuar con</p>
            <Divider className="flex-1" />
          </div>

          <Button
            onPress={() => googleLogin()}
            variant="bordered"
            className="w-full"
            startContent={<Icon icon="logos:google-icon" className="w-5 h-5" />}
          >
            Continuar con Google
          </Button>
        </CardBody>

        <CardFooter className="flex flex-col items-center gap-3 pt-0">
          <p className="text-tiny text-default-500">
            Al iniciar sesión, aceptas nuestros{" "}
            <a href="#" className="text-primary underline text-tiny">
              Términos de servicio
            </a>{" "}
            y{" "}
            <a href="#" className="text-primary underline text-tiny">
              Política de privacidad
            </a>
          </p>
        </CardFooter>
      </Card>
    </motion.div>
  );
};
